import { ArrowLeft, CheckCircleFilled } from '@brandfolder/icons';
import { brandfolderColorPalette } from '@brandfolder/react';
import { Trans, t } from '@lingui/macro';
import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import {
  createCustomPermission,
  deleteCustomPermission,
  updateCustomPermission
} from '@api/v4/organizations/custom_permissions';

import {
  PrimaryButton,
  SecondaryButton,
  DangerButton,
} from '@components/library/button';
import { StandardCheckbox } from '@components/library/checkbox';
import { StandardTextField } from '@components/library/text-field';

const CreateCustomRolePageWrapper = styled.div`
  background: ${brandfolderColorPalette.neutrals[0]};
  height: 100%;
  overflow-y: auto;
  padding: 24px 32px;
`;

const BackButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${brandfolderColorPalette.primary[700]};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: 4px;
  padding: 0;
  font-weight: 600;

  path {
    fill: ${brandfolderColorPalette.primary[700]};
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  color: ${brandfolderColorPalette.neutrals[800]};
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  color: ${brandfolderColorPalette.neutrals[800]};
  font-size: 20px;
  font-weight: 600;
`;

const PermissionGroup = styled.div`
  margin-bottom: 16px;
`;

const PermissionItem = styled.div`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 0;
  display: flex;
  margin-bottom: 16px;
  padding: 0;
`;

const PermissionContent = styled.div`
  flex: 1;
  margin-left: 8px;
`;

const PermissionTitle = styled.div`
  color: ${brandfolderColorPalette.neutrals[800]};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 2px;
`;

const PermissionDescription = styled.div`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 12px;
  line-height: 1.3;
`;

const DefaultCapabilities = styled.div`
  background-color: transparent;
  border: none;
  border-radius: 0;
  margin-top: 16px;
  padding: 0;
`;

const DefaultCapabilitiesTitle = styled.div`
  align-items: center;
  display: flex;
  font-family: 'Circular-Medium', 'Arial', 'Trebuchet MS', sans-serif;
  font-size: 14px;
  font-weight: 600;
  gap: 4px;
  margin: 0 0 8px 0;
`;

const CapabilityList = styled.ul`
  color: ${brandfolderColorPalette.neutrals[600]};
  font-size: 12px;
  margin: 0;
  padding-left: 16px;

  li {
    font-size: 12px;
    line-height: 1.3;
    margin-bottom: 2px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 32px;
  padding-top: 16px;
`;

const RightButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const StyledStandardCheckbox = styled(StandardCheckbox)`
  width: auto !important;
`;

const StyledCheckCircleFilled = styled(CheckCircleFilled)`
  path {
    fill: ${brandfolderColorPalette.success[800]};
  }
`;

const RoleNameInput = styled(StandardTextField)`
  width: 400px;
`;

interface CreateCustomRolePageProps {
  onBack: () => void;
  organizationKey?: string;
  existingRole?: {
    key: string;
    label: string;
    asset_approve: boolean;
    add_to_shares: boolean;
    view_asset_usage: boolean;
    view_collection_usage: boolean;
  };
  onSave?: () => void;
  onDelete?: () => void;
}

export const CreateCustomRolePage: FunctionComponent<CreateCustomRolePageProps> = ({
  onBack,
  organizationKey = BFG.organization_key,
  existingRole = undefined,
  onSave = undefined,
  onDelete = undefined,
}) => {
  const [roleName, setRoleName] = useState(existingRole?.label || '');
  const [permissions, setPermissions] = useState(existingRole ? {
    asset_approve: existingRole.asset_approve,
    add_to_shares: existingRole.add_to_shares,
    view_asset_usage: existingRole.view_asset_usage,
    view_collection_usage: existingRole.view_collection_usage,
  } : {
    asset_approve: false,
    add_to_shares: false,
    view_asset_usage: false,
    view_collection_usage: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingRole, setIsDeletingRole] = useState(false);
  const orgKey = organizationKey ||
    (window as unknown as { BFG?: { organization_key?: string } }).BFG?.organization_key;

  const handlePermissionChange = (
    permission: keyof typeof permissions
  ): void => {
    setPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };

  const handleSubmit = async (): Promise<void> => {
    if (!orgKey) {
      return;
    }

    if (!roleName.trim()) {
      return;
    }

    setIsLoading(true);

    try {
      if (existingRole?.key) {
        // Update existing role
        await updateCustomPermission(orgKey, existingRole.key, {
          label: roleName.trim(),
          ...permissions,
        });
      } else {
        // Create new role
        await createCustomPermission(orgKey, {
          label: roleName.trim(),
          ...permissions,
        });
      }

      onSave?.();
      onBack();
    } catch (error) {
      console.error('Error saving custom role:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!orgKey || !existingRole?.key) {
      return;
    }

    setIsDeletingRole(true);

    try {
      await deleteCustomPermission(orgKey, existingRole.key);

      onDelete?.();
      onBack();
    } catch (error) {
      console.error('Error deleting custom role:', error);
    } finally {
      setIsDeletingRole(false);
    }
  };

  return (
    <CreateCustomRolePageWrapper>
      <BackButton onClick={onBack} type='button'>
        <ArrowLeft />
        <Trans>Back</Trans>
      </BackButton>

      <PageHeader>
        <PageTitle>
          {existingRole ? <Trans>Edit custom role</Trans> : <Trans>Create custom role</Trans>}
        </PageTitle>
        <PageDescription>
          <Trans>
            This custom role includes all the capabilities of the Collaborator
            role by default. You can rename it and add more capabilities as
            needed.
          </Trans>
        </PageDescription>
      </PageHeader>

      <FormSection>
        <RoleNameInput
          id="role-name"
          label={t`Name`}
          onChange={(e): void => setRoleName(e.target.value)}
          placeholder={t`Collaborator Plus`}
          value={roleName}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>
          <Trans>Set permissions</Trans>
        </SectionTitle>
        <PageDescription style={{ marginBottom: '16px' }}>
          <Trans>Select the capabilities you want to give to this role.</Trans>
        </PageDescription>

        <PermissionGroup>
          <PermissionItem>
            <StyledStandardCheckbox
              checked={permissions.asset_approve}
              onChange={() => handlePermissionChange('asset_approve')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Approve assets</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>
                  Ability to approve, expire or reject assets for requests in Brandfolder
                </Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>

          <PermissionItem>
            <StyledStandardCheckbox
              checked={permissions.add_to_shares}
              onChange={() => handlePermissionChange('add_to_shares')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Manage share links</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>
                  Ability to view, add assets to any share link (owned by you or owned by another user)
                </Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>

          <PermissionItem>
            <StyledStandardCheckbox
              checked={permissions.view_asset_usage}
              onChange={() => handlePermissionChange('view_asset_usage')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Manage assets</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>Ability to change sections for uploaded assets</Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>

          <PermissionItem>
            <StyledStandardCheckbox
              checked={permissions.view_collection_usage}
              onChange={() => handlePermissionChange('view_collection_usage')}
            />
            <PermissionContent>
              <PermissionTitle>
                <Trans>Edit Brandguide</Trans>
              </PermissionTitle>
              <PermissionDescription>
                <Trans>Ability to edit a Brandguide</Trans>
              </PermissionDescription>
            </PermissionContent>
          </PermissionItem>
        </PermissionGroup>

        <DefaultCapabilities>
          <DefaultCapabilitiesTitle>
            <StyledCheckCircleFilled height={16} viewBox="0 0 16 16" width={16} />
            <Trans>Capabilities for this role by default:</Trans>
          </DefaultCapabilitiesTitle>
          <CapabilityList>
            <li>
              <Trans>Get Brandfolder embed code</Trans>
            </li>
            <li>
              <Trans>Create/rename sections on the Brandfolder level</Trans>
            </li>
            <li>
              <Trans>Upload assets to existing sections & subsections</Trans>
            </li>
            <li>
              <Trans>Add/edit/delete assets</Trans>
            </li>
            <li>
              <Trans>Merge assets into one asset container</Trans>
            </li>
            <li>
              <Trans>Edit asset details</Trans>
            </li>
            <li>
              <Trans>
                Set availability & expire assets at the Brandfolder level (does
                not apply to Collection level)
              </Trans>
            </li>
            <li>
              <Trans>
                Change the type & size (width & height, maintaining aspect
                ratio)
              </Trans>
            </li>
            <li>
              <Trans>Download assets from Brandfolder</Trans>
            </li>
            <li>
              <Trans>
                Access templates, edit form fields, edit template settings
              </Trans>
            </li>
          </CapabilityList>
        </DefaultCapabilities>
      </FormSection>

      <ButtonGroup>
        {existingRole ? <DangerButton
          disabled={isDeletingRole || isLoading}
          onClick={() => void handleDelete()}
        >
          {isDeletingRole ? <Trans>Deleting...</Trans> : <Trans>Delete</Trans>}
        </DangerButton> : <div></div>}
        <RightButtons>
          <SecondaryButton
            disabled={isLoading || isDeletingRole}
            onClick={onBack}
          >
            <Trans>Back</Trans>
          </SecondaryButton>
          <PrimaryButton
            disabled={isLoading || isDeletingRole}
            onClick={() => void handleSubmit()}
          >
            {isLoading ?
              (existingRole ?
              <Trans>Updating...</Trans> :
              <Trans>Creating...</Trans>) :
              (existingRole ?
              <Trans>Update role</Trans> :
              <Trans>Create role</Trans>)}
          </PrimaryButton>
        </RightButtons>
      </ButtonGroup>
    </CreateCustomRolePageWrapper>
  );
};
